import type { CSSProperties } from 'react';
import { ArrowLeft } from '@phosphor-icons/react/ArrowLeft';
import { ArrowRight } from '@phosphor-icons/react/ArrowRight';
import { ArrowsClockwise } from '@phosphor-icons/react/ArrowsClockwise';
import { At } from '@phosphor-icons/react/At';
import { CaretDown } from '@phosphor-icons/react/CaretDown';
import { Check } from '@phosphor-icons/react/Check';
import { Compass } from '@phosphor-icons/react/Compass';
import { Copy } from '@phosphor-icons/react/Copy';
import { CornersIn } from '@phosphor-icons/react/CornersIn';
import { CornersOut } from '@phosphor-icons/react/CornersOut';
import { DotsThree } from '@phosphor-icons/react/DotsThree';
import { DownloadSimple } from '@phosphor-icons/react/DownloadSimple';
import { EnvelopeSimple } from '@phosphor-icons/react/EnvelopeSimple';
import { Eye } from '@phosphor-icons/react/Eye';
import { File } from '@phosphor-icons/react/File';
import { Folder } from '@phosphor-icons/react/Folder';
import { FolderOpen } from '@phosphor-icons/react/FolderOpen';
import { GearSix } from '@phosphor-icons/react/GearSix';
import { Hash } from '@phosphor-icons/react/Hash';
import { Headphones } from '@phosphor-icons/react/Headphones';
import { IdentificationCard } from '@phosphor-icons/react/IdentificationCard';
import { Image } from '@phosphor-icons/react/Image';
import { Key } from '@phosphor-icons/react/Key';
import { LinkSimple } from '@phosphor-icons/react/LinkSimple';
import { ListChecks } from '@phosphor-icons/react/ListChecks';
import { LockSimple } from '@phosphor-icons/react/LockSimple';
import { MagnifyingGlass } from '@phosphor-icons/react/MagnifyingGlass';
import { Megaphone } from '@phosphor-icons/react/Megaphone';
import { Microphone } from '@phosphor-icons/react/Microphone';
import { Minus } from '@phosphor-icons/react/Minus';
import { MonitorArrowUp } from '@phosphor-icons/react/MonitorArrowUp';
import { Moon } from '@phosphor-icons/react/Moon';
import { PaperPlaneTilt } from '@phosphor-icons/react/PaperPlaneTilt';
import { PencilSimple } from '@phosphor-icons/react/PencilSimple';
import { Phone } from '@phosphor-icons/react/Phone';
import { Plus } from '@phosphor-icons/react/Plus';
import { Prohibit } from '@phosphor-icons/react/Prohibit';
import { PushPin } from '@phosphor-icons/react/PushPin';
import { Question } from '@phosphor-icons/react/Question';
import { ShieldCheck } from '@phosphor-icons/react/ShieldCheck';
import { SignOut } from '@phosphor-icons/react/SignOut';
import { Smiley } from '@phosphor-icons/react/Smiley';
import { SpeakerHigh } from '@phosphor-icons/react/SpeakerHigh';
import { Square } from '@phosphor-icons/react/Square';
import { Sun } from '@phosphor-icons/react/Sun';
import { Trash } from '@phosphor-icons/react/Trash';
import { User } from '@phosphor-icons/react/User';
import { UserPlus } from '@phosphor-icons/react/UserPlus';
import { UsersThree } from '@phosphor-icons/react/UsersThree';
import { VideoCamera } from '@phosphor-icons/react/VideoCamera';
import { Warning } from '@phosphor-icons/react/Warning';
import { X } from '@phosphor-icons/react/X';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';

const ICONS = {
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  at: At,
  card: IdentificationCard,
  caret: CaretDown,
  check: Check,
  close: X,
  compass: Compass,
  contract: CornersIn,
  copy: Copy,
  dots: DotsThree,
  download: DownloadSimple,
  eye: Eye,
  expand: CornersOut,
  file: File,
  folder: Folder,
  folderOpen: FolderOpen,
  gear: GearSix,
  hash: Hash,
  headphones: Headphones,
  image: Image,
  key: Key,
  leave: SignOut,
  link: LinkSimple,
  listChecks: ListChecks,
  lock: LockSimple,
  mail: EnvelopeSimple,
  megaphone: Megaphone,
  members: UsersThree,
  mic: Microphone,
  minus: Minus,
  moon: Moon,
  pencil: PencilSimple,
  phone: Phone,
  pin: PushPin,
  plus: Plus,
  question: Question,
  refresh: ArrowsClockwise,
  screen: MonitorArrowUp,
  search: MagnifyingGlass,
  send: PaperPlaneTilt,
  shield: ShieldCheck,
  slash: Prohibit,
  smiley: Smiley,
  speaker: SpeakerHigh,
  square: Square,
  sun: Sun,
  trash: Trash,
  user: User,
  userPlus: UserPlus,
  video: VideoCamera,
  voice: SpeakerHigh,
  warning: Warning,
} as const satisfies Record<string, PhosphorIcon>;

export type AppIconName = keyof typeof ICONS;

export interface AppIconProps {
  name: AppIconName;
  size?: number;
  /** Kept while callers migrate from the former cut-out icon API. */
  hole?: string;
  style?: CSSProperties;
  slashed?: boolean;
  className?: string;
  title?: string;
}

/**
 * One calm outline vocabulary across the product.
 *
 * Icons never carry meaning alone: interactive call sites keep their visible
 * text or accessible label. `slashed` adds a smooth vector stroke without
 * creating a second icon set for every disabled state.
 */
export function AppIcon({
  name,
  size = 18,
  style,
  slashed = false,
  className,
  title,
}: AppIconProps): React.ReactElement {
  const Glyph = ICONS[name];
  const labelProps =
    title === undefined
      ? ({ 'aria-hidden': true } as const)
      : ({ role: 'img', 'aria-label': title } as const);

  return (
    <span
      className={className}
      style={{
        position: 'relative',
        display: 'inline-grid',
        placeItems: 'center',
        width: size,
        height: size,
        flex: 'none',
        ...style,
      }}
      {...labelProps}
    >
      <Glyph size={size} weight={size <= 14 ? 'bold' : 'regular'} />
      {slashed ? (
        <svg
          viewBox="0 0 24 24"
          width={size}
          height={size}
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0 }}
        >
          <path
            d="M4 4 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ) : null}
    </span>
  );
}
